import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save, Loader2, Banknote, Info } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { adminApi } from '@/services/api';
import { cn } from '@nepal-ecom/ui';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@nepal-ecom/ui';

interface CodConfig {
  enabled: boolean;
  label: string;
  instructions: string;
}

interface PaymentMethods {
  cod: CodConfig;
}

interface SettingsResponse {
  paymentMethods?: Partial<PaymentMethods>;
  [k: string]: unknown;
}

const EMPTY_COD: CodConfig = { enabled: true, label: 'Cash on Delivery', instructions: '' };

export function Payments() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminApi.getPlatformSettings(),
    select: (res) => res.data.data as SettingsResponse,
  });

  const [cod, setCod] = useState<CodConfig>(EMPTY_COD);

  useEffect(() => {
    if (!data?.paymentMethods) return;
    if (data.paymentMethods.cod) setCod({ ...EMPTY_COD, ...data.paymentMethods.cod });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updatePlatformSettings({
        paymentMethods: { cod },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Payment methods saved');
    },
    onError: (e: any) =>
      toast.error('Save failed', { description: e?.response?.data?.message ?? String(e) }),
  });

  const save = () => {
    saveMutation.mutate();
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Payment methods"
        description="Manual payment workflow. Customers pay via Cash on Delivery — no automated gateway."
        actions={
          <Button onClick={save} disabled={saveMutation.isPending || isLoading}>
            {saveMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            Save changes
          </Button>
        }
      />

      {/* Workflow callout */}
      <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-3.5 flex items-start gap-3">
        <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          <p className="font-semibold mb-0.5">How manual payments work</p>
          <p>
            Customers check out with <strong>Cash on Delivery</strong> — the order goes straight into
            processing and your delivery agent collects cash on arrival. Track and confirm orders on
            the <a href="/orders" className="underline font-medium">Orders</a> page.
          </p>
        </div>
      </div>

      <div className="max-w-2xl">
        {/* ─────────── COD ─────────── */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-emerald-600" /> Cash on Delivery
              </CardTitle>
              <Toggle value={cod.enabled} onChange={(v) => setCod({ ...cod, enabled: v })} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Label shown at checkout">
              <Input
                value={cod.label}
                onChange={(e) => setCod({ ...cod, label: e.target.value })}
                placeholder="e.g. Cash on Delivery"
                disabled={!cod.enabled}
              />
            </Field>
            <Field label="Instructions for the customer">
              <textarea
                rows={3}
                value={cod.instructions}
                onChange={(e) => setCod({ ...cod, instructions: e.target.value })}
                placeholder="Pay our delivery agent in cash when your order arrives."
                disabled={!cod.enabled}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 disabled:bg-slate-50 disabled:text-slate-400"
              />
            </Field>
            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Preview</p>
              <div className="flex items-start gap-2">
                <Banknote className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{cod.label || 'Cash on Delivery'}</p>
                  <p className="text-xs text-slate-600">{cod.instructions || 'No instructions yet.'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
        value ? 'bg-primary-600' : 'bg-slate-300',
      )}
      aria-pressed={value}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          value ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  );
}
